import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { RegistryPager, useRegistryPage, useSelectedRecordParam } from './registryPaging';
import {
  apiErrorText,
  useAnalyzeZviCheckMutation,
  useAnnotateZviDiscrepancyMutation,
  useCreateSmetaRequestMutation,
  useCreateZviCheckMutation,
  useDeleteZviCheckMutation,
  useGetZviCheckQuery,
  useLazyExportZviCheckXlsxQuery,
  useListContractEstimateOptionsQuery,
  useListZviChecksQuery,
} from '@shared/api';
import type { ContractEstimateOption, ZviDiscrepancy, ZviItem } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Label, Field } from '@shared/ui/Input';
import { Combobox } from '@shared/ui/Combobox';
import { useAgentPermission } from '@entities/user';
import { useAppSelector } from '@app/hooks';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import {
  decisionLabel,
  fmtMoney,
  fmtNum,
  severityTone,
} from '@shared/lib/smetaLabels';
import { ParseWarningsBanner } from './ParseWarningsBanner';
import { DecisionSaveCaption } from './DecisionSaveError';
import { useDecisionSave } from './decisionSave';
import { estimateVersionLabel } from './contractEstimateLabels';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Row = styled.div<{ $mt?: number }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  ${({ $mt }) => ($mt !== undefined ? `margin-top: ${$mt}px;` : '')}
`;

const BadgeWrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const CheckRow = styled.button<{ $active?: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  font: inherit;
  color: inherit;
  transition: border-color 120ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const ObjName = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div<{ $mt?: number }>`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  ${({ $mt }) => ($mt !== undefined ? `margin-top: ${$mt}px;` : '')}
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const TableTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const FileHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const DecisionBtns = styled.div`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/** Терминальные статусы — сверка завершена, поллинг можно гасить. */
const TERMINAL = new Set(['completed', 'failed']);

function statusTone(status: string): Tone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'calculating':
    case 'parsing':
      return 'warning';
    default:
      return 'neutral';
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Загружен';
    case 'parsing':
      return 'Разбор';
    case 'parsed':
      return 'Распознан';
    case 'calculating':
      return 'Сверка';
    case 'completed':
      return 'Проверен';
    case 'failed':
      return 'Ошибка';
    default:
      return status;
  }
}

function scopeLabel(d: ZviDiscrepancy): string {
  if (d.resource_id) return 'Ресурс';
  if (d.item_id) return 'Позиция';
  return 'Сессия';
}

/** Подпись варианта договорной сметы для выпадающего списка: понятный номер
 *  договора + предмет + версия (формат, статус, позиции, сумма, дата) + сколько
 *  позиций несут цену (для сверки ставок). */
function estimateOptionLabel(o: ContractEstimateOption): string {
  const num = o.contract_number ? `№ ${o.contract_number}` : 'договор без №';
  const who = (o.subject?.trim() || o.counterparty || '').slice(0, 60);
  return `${num} · ${who} · ${estimateVersionLabel(o)} · с ценой ${o.priced_count} из ${o.item_count}`;
}

function PositionsTable({ items }: { items: ZviItem[] }) {
  if (items.length === 0) return <Muted>позиций нет</Muted>;
  return (
    <Table>
      <thead>
        <tr>
          <th>Код ЕР</th>
          <th>Наименование</th>
          <th>Ед.</th>
          <th className="num">Договор</th>
          <th className="num">РД</th>
          <th className="num">Δ</th>
          <th className="num">Ставка ПЗ</th>
          <th className="num">Эталон</th>
          <th>Статус</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it.id}>
            <td>{it.code ?? '—'}</td>
            <td>{it.name}</td>
            <td>{it.unit ?? '—'}</td>
            <td className="num">{fmtNum(it.contract_qty)}</td>
            <td className="num">{fmtNum(it.rd_qty)}</td>
            <td className="num">{fmtNum(it.delta_qty)}</td>
            <td className="num">{fmtMoney(it.claimed_direct_unit)}</td>
            <td className="num">{fmtMoney(it.norm_direct_unit)}</td>
            <td>
              {it.is_new_rate ? (
                <Badge $tone="warning">новая ЕР</Badge>
              ) : it.matched ? (
                <Badge $tone="success">сверено</Badge>
              ) : (
                <Badge $tone="neutral">—</Badge>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function Discrepancies({
  items,
  checkId,
}: {
  items: ZviDiscrepancy[];
  checkId: string;
}) {
  const [annotate, { isLoading }] = useAnnotateZviDiscrepancyMutation();
  const { error: saveError, save } = useDecisionSave();
  const { can_write } = useAgentPermission('smeta');

  if (items.length === 0) {
    return <Muted>✓ расхождений при сверке ЗВИ не выявлено</Muted>;
  }

  function decide(id: string, decision: string) {
    void save(() => annotate({ id, checkId, decision }));
  }

  return (
    <Table>
      <DecisionSaveCaption message={saveError} />
      <thead>
        <tr>
          <th>Крит.</th>
          <th>Уровень</th>
          <th>Сообщение</th>
          <th className="num">Заявл.</th>
          <th className="num">Норматив</th>
          <th>Решение</th>
        </tr>
      </thead>
      <tbody>
        {items.map((d) => (
          <tr key={d.id}>
            <td>
              <Badge $tone={severityTone(d.severity)}>{d.severity}</Badge>
            </td>
            <td>{scopeLabel(d)}</td>
            <td>{d.message}</td>
            <td className="num">{fmtMoney(d.claimed_value)}</td>
            <td className="num">{fmtMoney(d.normative_value)}</td>
            <td>
              {d.decision ? (
                <Badge $tone="neutral">{decisionLabel(d.decision)}</Badge>
              ) : (
                <DecisionBtns>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => decide(d.id, 'accepted')}
                  >
                    Принять
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => decide(d.id, 'disputed')}
                  >
                    Спорно
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => decide(d.id, 'rejected')}
                  >
                    Отклонить
                  </Button>
                </DecisionBtns>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function ZviChecks() {
  const { activeProjectName } = useActiveProjectContext();
  const [objectName, setObjectName] = useState(activeProjectName ?? '');
  const [contractEstimateId, setContractEstimateId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [bomFiles, setBomFiles] = useState<FileList | null>(null);
  const [boqFiles, setBoqFiles] = useState<FileList | null>(null);
  const [selectedId, setSelectedId] = useSelectedRecordParam();
  const [poll, setPoll] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [requestNotice, setRequestNotice] = useState<string | null>(null);
  const { can_write } = useAgentPermission('smeta');
  const currentUserId = useAppSelector((s) => s.auth.user?.id);

  useEffect(() => {
    if (activeProjectName && !objectName) {
      setObjectName(activeProjectName);
    }
  }, [activeProjectName, objectName]);

  const page = useRegistryPage();
  const listQ = useListZviChecksQuery(
    { limit: page.limit, offset: page.offset },
    { pollingInterval: 5000 },
  );
  const [createCheck, { isLoading: creating }] = useCreateZviCheckMutation();
  const [deleteCheck] = useDeleteZviCheckMutation();
  const [analyzeCheck, { isLoading: analyzing }] = useAnalyzeZviCheckMutation();
  const [createRequest, { isLoading: sendingRequest }] = useCreateSmetaRequestMutation();
  const detailQ = useGetZviCheckQuery(selectedId ?? '', {
    skip: !selectedId,
    pollingInterval: poll,
  });
  const [exportXlsx, { isFetching: exporting }] = useLazyExportZviCheckXlsxQuery();
  const estimateOptionsQ = useListContractEstimateOptionsQuery();
  const estimateOptions = estimateOptionsQ.data?.items ?? [];

  useEffect(() => {
    if (!selectedId) {
      setPoll(0);
      return;
    }
    const st = detailQ.data?.check.status;
    setPoll(st && !TERMINAL.has(st) ? 2500 : 0);
  }, [selectedId, detailQ.data?.check.status]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Приложите файл формы ЗВИ (xlsx).');
      return;
    }
    const fd = new FormData();
    fd.append('object_name', objectName);
    const validEstimateId = estimateOptions.some((o) => o.id === contractEstimateId)
      ? contractEstimateId
      : '';
    if (validEstimateId) fd.append('contract_estimate_id', validEstimateId);
    fd.append('file', file);
    if (bomFiles) {
      Array.from(bomFiles).forEach((f) => fd.append('bom', f));
    }
    if (boqFiles) {
      Array.from(boqFiles).forEach((f) => fd.append('boq', f));
    }
    try {
      const res = await createCheck(fd).unwrap();
      setSelectedId(res.check_id);
      setObjectName('');
      setContractEstimateId('');
      setFile(null);
      setBomFiles(null);
      setBoqFiles(null);
    } catch (err) {
      setError(
        apiErrorText(err, 'Не удалось загрузить ЗВИ. Проверьте файл формы (лист «ЗВИ»).'),
      );
      // eslint-disable-next-line no-console
      console.error('createZviCheck failed', err);
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteCheck(id).unwrap();
      if (selectedId === id) setSelectedId(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('deleteZviCheck failed', err);
    }
  }

  async function handleExport(id: string) {
    setExportError(null);
    try {
      const blob = await exportXlsx(id).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zvi_check_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError('Не удалось выгрузить XLSX.');
      // eslint-disable-next-line no-console
      console.error('exportZviCheckXlsx failed', err);
    }
  }

  async function handleSendDsRequest() {
    if (!detail) return;
    setRequestNotice(null);
    setExportError(null);
    const zviLabel = detail.check.zvi_number
      ? `ЗВИ № ${detail.check.zvi_number}`
      : detail.check.object_name;
    try {
      await createRequest({
        kind: 'contract_price_revision',
        contract_id: detail.check.contract_id ?? undefined,
        project_id: detail.check.project_id ?? undefined,
        title: `Подготовка ДС по ${zviLabel}`,
        summary: `Сметно-ценовой отдел проверил ${zviLabel} (${detail.items.length} поз., расхождений: ${detail.discrepancies.length}). Требуется подготовка дополнительного соглашения на пересмотр сметной стоимости.`,
        details: {
          zvi_check_id: detail.check.id,
          zvi_number: detail.check.zvi_number,
          contract_no: detail.check.contract_no,
          position_count: detail.items.length,
          discrepancy_count: detail.discrepancies.length,
        },
      }).unwrap();
      setRequestNotice('Заявка на подготовку ДС отправлена в Юриста (Статут) / Экономиста.');
    } catch {
      setExportError('Не удалось отправить заявку на ДС.');
    }
  }

  const checks = listQ.data?.items ?? [];
  const detail = detailQ.data;
  const status = detail?.check.status;
  const isAuthor =
    !detail?.check.user_id ||
    !currentUserId ||
    detail.check.user_id === currentUserId;
  const estimateComboOptions = estimateOptions.map((o) => ({
    id: o.id,
    label: estimateOptionLabel(o),
    sublabel: o.comment ?? undefined,
  }));
  const selectedEstimate = estimateOptions.find((o) => o.id === contractEstimateId);
  const selectedEstimateLabel = selectedEstimate ? estimateOptionLabel(selectedEstimate) : '';

  return (
    <Stack>
      <Card>
        <form onSubmit={handleSubmit}>
          <Stack>
            <Field>
              <Label htmlFor="zvi-object">Объект / договор</Label>
              <Input
                id="zvi-object"
                value={objectName}
                onChange={(e) => setObjectName(e.target.value)}
                placeholder="напр. КНС бытовых сточных вод"
              />
            </Field>
            <Field>
              <Label htmlFor="zvi-estimate">Договорная смета (РДЦ) — для сверки ставок ЕР</Label>
              <Combobox
                id="zvi-estimate"
                value={contractEstimateId}
                selectedLabel={selectedEstimateLabel}
                options={estimateComboOptions}
                disabled={estimateOptions.length === 0}
                placeholder="Поиск по № договора / предмету (или оставьте для авто-привязки)"
                emptyText="РДЦ не найдены"
                onChange={(opt) => setContractEstimateId(opt?.id ?? '')}
              />
              <FileHint>
                {estimateOptions.length === 0
                  ? 'Договорные сметы (РДЦ) ещё не загружены. Загрузите РДЦ/ЛСР в разделе «Проверка КС-2/КС-3» — после этого она появится здесь. Без неё ставки ЕР и новые расценки не сверяются.'
                  : 'Начните вводить № договора или предмет. Если не выбирать — система попробует привязать активную РДЦ по номеру договора из формы ЗВИ.'}
              </FileHint>
            </Field>
            <Field>
              <Label htmlFor="zvi-file">Форма ЗВИ (xlsx)</Label>
              <Input
                id="zvi-file"
                type="file"
                accept=".xlsx,.xlsm"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
              {file && <FileHint>{file.name}</FileHint>}
            </Field>
            <Field>
              <Label htmlFor="zvi-bom">Ведомости материалов BOM (PDF, опц.)</Label>
              <Input
                id="zvi-bom"
                type="file"
                accept=".pdf"
                multiple
                onChange={(e) => setBomFiles(e.target.files)}
              />
            </Field>
            <Field>
              <Label htmlFor="zvi-boq">Ведомости объёмов BOQ (PDF, опц.)</Label>
              <Input
                id="zvi-boq"
                type="file"
                accept=".pdf"
                multiple
                onChange={(e) => setBoqFiles(e.target.files)}
              />
            </Field>
            {error && <Err>{error}</Err>}
            <div>
              <Button
                type="submit"
                $variant="primary"
                disabled={creating || !can_write}
                title={!can_write ? 'Только чтение' : undefined}
              >
                {creating ? 'Загрузка…' : 'Загрузить и сверить ЗВИ'}
              </Button>
            </div>
          </Stack>
        </form>
      </Card>

      <Grid>
        <Stack>
          <RegistryPager
            label="Загружено ЗВИ"
            page={page}
            shown={checks.length}
            hasMore={listQ.data?.has_more}
            fetching={listQ.isFetching}
          />
          {checks.length === 0 && <Muted>Загруженных ЗВИ пока нет.</Muted>}
          {checks.map((c) => (
            <CheckRow
              key={c.id}
              $active={c.id === selectedId}
              onClick={() => setSelectedId(c.id)}
            >
              <ObjName>{c.zvi_number ? `ЗВИ № ${c.zvi_number}` : c.object_name}</ObjName>
              <Muted>{c.object_name}</Muted>
              <Row>
                <Badge $tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
                <Muted>{c.position_count} поз.</Muted>
              </Row>
            </CheckRow>
          ))}
        </Stack>

        <div>
          {!detail && <Muted>Выберите ЗВИ слева для детализации.</Muted>}
          {detail && (
            <Stack>
              <ParseWarningsBanner warnings={detail.warnings} />
              <Card>
              <Row>
                <div>
                  <ObjName>
                    {detail.check.zvi_number
                      ? `ЗВИ № ${detail.check.zvi_number}`
                      : detail.check.object_name}
                  </ObjName>
                  <Muted>
                    {detail.check.contract_no ? `Договор: ${detail.check.contract_no} · ` : ''}
                    {detail.check.object_name}
                  </Muted>
                  <BadgeWrap>
                    {detail.check.contract_estimate_id ? (
                      <Badge $tone="success">РДЦ привязана · ставки ЕР сверяются</Badge>
                    ) : (
                      <Badge $tone="warning">РДЦ не привязана · ставки ЕР не сверяются</Badge>
                    )}
                  </BadgeWrap>
                </div>
                <Badge $tone={statusTone(detail.check.status)}>
                  {statusLabel(detail.check.status)}
                </Badge>
              </Row>

              <Row $mt={12}>
                <DecisionBtns>
                  <Button
                    $variant="secondary"
                    $size="sm"
                    disabled={analyzing || !can_write || !status || !TERMINAL.has(status)}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => analyzeCheck(detail.check.id)}
                  >
                    Пересверить
                  </Button>
                  <Button
                    $variant="secondary"
                    $size="sm"
                    disabled={exporting || status !== 'completed'}
                    onClick={() => handleExport(detail.check.id)}
                  >
                    Скачать XLSX
                  </Button>
                  {status === 'completed' && (
                    <Button
                      $variant="secondary"
                      $size="sm"
                      disabled={sendingRequest || !can_write}
                      title="Отправить межфункциональную заявку на подготовку ДС в Статут / Экономиста"
                      onClick={handleSendDsRequest}
                    >
                      {sendingRequest ? 'Отправляю…' : 'Заявка на ДС (Юристу)'}
                    </Button>
                  )}
                  <Button
                    $variant="danger"
                    $size="sm"
                    disabled={!can_write || !isAuthor}
                    title={
                      !can_write
                        ? 'Только чтение'
                        : !isAuthor
                          ? 'Удалить проверку может только её автор'
                          : undefined
                    }
                    onClick={() => handleDelete(detail.check.id)}
                  >
                    Удалить
                  </Button>
                </DecisionBtns>
              </Row>

              {requestNotice && <Muted $mt={8}>{requestNotice}</Muted>}
              {detail.check.status === 'failed' && detail.check.error_message && (
                <Err>{detail.check.error_message}</Err>
              )}
              {exportError && <Err>{exportError}</Err>}

              <TableTitle>Позиции ЗВИ</TableTitle>
              <PositionsTable items={detail.items} />

              <TableTitle>Расхождения сверки</TableTitle>
              <Discrepancies items={detail.discrepancies} checkId={detail.check.id} />
              </Card>
            </Stack>
          )}
        </div>
      </Grid>
    </Stack>
  );
}
